import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styles from './StoryPage.module.scss';
import productService from '../../services/productService';
import { useLoading } from '../../context/LoadingContext/LoadingContext';
import { Helmet } from 'react-helmet-async';
import { IoTimeOutline, IoArrowBack, IoClose, IoListOutline } from 'react-icons/io5';
import heroBg from '../../assets/images/story-bg.jpg';

// --- IMPORT LIGHTBOX ---
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom"; 
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails"; 
import "yet-another-react-lightbox/plugins/thumbnails.css";

const StoryPage = () => {
  const [pageData, setPageData] = useState(null);
  const [processedContent, setProcessedContent] = useState('');
  const [toc, setToc] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [activeTitle, setActiveTitle] = useState('');
  const [showMobileToc, setShowMobileToc] = useState(false);
  const [isScrolledPastHero, setIsScrolledPastHero] = useState(false);
  
  // --- STATE LIGHTBOX ---
  const [openLightbox, setOpenLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [gallerySlides, setGallerySlides] = useState([]);

  // Ref để truy cập DOM nội dung bài viết
  const contentRef = useRef(null);
  const { showLoading, hideLoading } = useLoading();

  useEffect(() => {
    const fetchData = async () => {
      try {
        showLoading();
        const res = await productService.getPageContent('cau-chuyen-tram');
        setPageData(res.data);

        if (res.data && res.data.content) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(res.data.content, 'text/html');
          
          // 1. Xử lý Mục lục (TOC)
          const headings = doc.querySelectorAll('h2, h3');
          const tocList = [];
          headings.forEach((heading, index) => {
            const id = `heading-${index}`;
            heading.id = id;
            tocList.push({
              id: id,
              text: heading.innerText,
              level: heading.tagName
            });
          });
          setToc(tocList);
          if (tocList.length > 0) setActiveTitle(tocList[0].text);

          // 2. Xử lý Ẩn/Hiện dạng GRID (Accordion)
          const toggleLinks = doc.querySelectorAll('a[href="#toggle-images"]');
          
          toggleLinks.forEach((link, index) => {
            const toggleId = `toggle-section-${index}`;
            link.setAttribute('id', toggleId);
            link.classList.add('toggle-trigger'); 
            link.removeAttribute('href'); // Xóa href để tránh reload trang
            
            // Tạo container Lưới (Grid) để chứa ảnh
            const gridContainer = doc.createElement('div');
            gridContainer.className = 'cert-grid-wrapper'; 
            gridContainer.setAttribute('id', `content-${toggleId}`);
            gridContainer.style.display = 'none'; // Mặc định ẩn

            // Tìm các ảnh nằm ngay sau đoạn chứa link này
            // (Trong Quill, link nằm trong thẻ P, các ảnh tiếp theo thường nằm trong các thẻ P hoặc IMG kế tiếp)
            let nextSibling = link.parentElement.nextElementSibling;
            const nodesToMove = []; 

            while (nextSibling) {
                // Kiểm tra xem thẻ này có chứa ảnh không
                const imgInside = nextSibling.querySelector('img') || (nextSibling.tagName === 'IMG' ? nextSibling : null);
                
                if (imgInside) {
                    // Tạo một div bọc từng ảnh để style (Card item)
                    const itemWrapper = doc.createElement('div');
                    itemWrapper.className = 'cert-item';
                    itemWrapper.appendChild(imgInside.cloneNode(true)); // Copy ảnh vào wrapper mới
                    
                    gridContainer.appendChild(itemWrapper); // Thêm vào lưới
                    
                    // Đánh dấu để xóa node cũ đi sau khi vòng lặp gom xong
                    nodesToMove.push(nextSibling);
                    
                    nextSibling = nextSibling.nextElementSibling;
                } else {
                    // Nếu gặp text thường hoặc thẻ khác -> Dừng gom nhóm
                    break; 
                }
            }

            // Xóa các node cũ ở ngoài đã được chuyển vào trong Grid
            nodesToMove.forEach(node => node.remove());

            // Chèn Grid Container vào ngay sau thẻ chứa link
            if (gridContainer.children.length > 0) {
                link.parentElement.insertAdjacentElement('afterend', gridContainer);
                link.innerHTML += ` <span class="toggle-icon">▼</span>`;
            }
          });

          // 3. Xử lý Lightbox (Quét lại toàn bộ ảnh sau khi đã sắp xếp DOM)
          const images = doc.querySelectorAll('img');
          const slides = [];
          
          images.forEach((img, index) => {
            // Gán index để khi click biết mở ảnh nào
            img.setAttribute('data-index', index);
            img.classList.add('lightbox-trigger'); // Class để CSS (cursor: zoom-in)
            
            slides.push({ 
                src: img.getAttribute('src'), 
                alt: img.getAttribute('alt') || 'Story Image' 
            });
          });
          setGallerySlides(slides);

          setProcessedContent(doc.body.innerHTML);
        }

      } catch (err) {
        console.error(err);
      } finally {
        hideLoading();
      }
    };
    fetchData();
  }, []);

  // --- HÀM XỬ LÝ CLICK (Event Delegation) ---
  const handleContentClick = useCallback((e) => {
    const target = e.target;

    // A. Click vào Nút Ẩn/Hiện (Toggle Link)
    const trigger = target.closest('.toggle-trigger');
    if (trigger) {
        e.preventDefault();
        const toggleId = trigger.id;
        const icon = trigger.querySelector('.toggle-icon');
        
        // Tìm Grid Container tương ứng
        const gridContent = contentRef.current.querySelector(`#content-${toggleId}`);
        
        if (gridContent) {
            const isHidden = gridContent.style.display === 'none';
            if (isHidden) {
                gridContent.style.display = 'grid'; // Chuyển sang display: grid
                gridContent.style.animation = 'fadeIn 0.5s ease';
                trigger.classList.add('active');
                if(icon) icon.textContent = '▲';
            } else {
                gridContent.style.display = 'none';
                trigger.classList.remove('active');
                if(icon) icon.textContent = '▼';
            }
        }
        return; // Dừng xử lý
    }

    // B. Click vào ẢNH (Mở Lightbox)
    if (target.tagName === 'IMG' && target.classList.contains('lightbox-trigger')) {
        const index = parseInt(target.getAttribute('data-index'), 10);
        if (!isNaN(index)) {
            setLightboxIndex(index);
            setOpenLightbox(true);
        }
    }
  }, []);

  // --- Scroll Spy Logic ---
  useEffect(() => {
    if (toc.length === 0) return;
    const handleScroll = () => {
      const heroElement = document.querySelector(`.${styles.hero}`);
      const heroHeight = heroElement ? heroElement.offsetHeight : 400;
      setIsScrolledPastHero(window.scrollY > heroHeight - 100);

      const headingElements = toc.map(item => document.getElementById(item.id)).filter(el => el);
      let currentActiveId = '';
      const offset = 150; 

      for (let i = 0; i < headingElements.length; i++) {
        const el = headingElements[i];
        const rect = el.getBoundingClientRect();
        if (rect.top < offset && rect.top > -rect.height) { 
          currentActiveId = el.id;
        } else if (rect.top < offset) {
           currentActiveId = el.id;
        }
      }
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 50 && headingElements.length > 0) {
         currentActiveId = headingElements[headingElements.length - 1].id;
      }
      if (currentActiveId && currentActiveId !== activeId) {
        setActiveId(currentActiveId);
        const activeItem = toc.find(t => t.id === currentActiveId);
        if (activeItem) setActiveTitle(activeItem.text);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [toc, activeId]);

  const scrollToHeading = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setShowMobileToc(false);
    }
  };

  if (!pageData) return null;

  const bannerImage = pageData.featured_image || heroBg;
  const wordCount = pageData.content ? pageData.content.split(' ').length : 0;
  const readingTime = Math.ceil(wordCount / 200) + ' phút đọc';

  // --- [SEO START] ---
  const currentUrl = window.location.href;
  const seoDescription = pageData.content 
    ? pageData.content.replace(/<[^>]+>/g, ' ').slice(0, 160).trim() + '...' 
    : 'Khám phá câu chuyện về hành trình mang hương trầm tự nhiên đến với mọi người của Hương Trầm Cổ Mộc.';
  
  // Đảm bảo ảnh banner là đường dẫn tuyệt đối cho SEO
  const seoImage = bannerImage.startsWith('http') 
    ? bannerImage 
    : `https://huongtramcomoc.vn${bannerImage}`;

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": pageData.title,
    "image": [seoImage],
    "author": {
      "@type": "Organization",
      "name": "Hương Trầm Cổ Mộc"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Hương Trầm Cổ Mộc",
      "logo": {
        "@type": "ImageObject",
        "url": "https://huongtramcomoc.vn/logo.png"
      }
    },
    "description": seoDescription,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": currentUrl
    }
  };
  // --- [SEO END] ---

  return (
    <div className={styles.wrapper}>
      {/* --- HELMET SEO --- */}
      <Helmet>
        <title>{pageData.title} - Hương Trầm Cổ Mộc</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={currentUrl} />

        {/* Facebook / Zalo Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={pageData.title} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:site_name" content="Hương Trầm Cổ Mộc" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageData.title} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>
      {/* ----------------- */}

      {/* MOBILE BAR */}
      <div className={`${styles.mobileStickyBar} ${isScrolledPastHero ? styles.visible : ''}`}>
        <div className={styles.barContainer}>
          <div className={styles.readingInfo} onClick={() => setShowMobileToc(!showMobileToc)}>
             <span className={styles.label}>ĐANG ĐỌC:</span>
             <span className={styles.currentTitle}>{activeTitle || 'Mục lục'}</span>
          </div>
          <button 
            className={`${styles.toggleBtn} ${showMobileToc ? styles.open : ''}`}
            onClick={() => setShowMobileToc(!showMobileToc)}
          >
            {showMobileToc ? <IoClose /> : <IoListOutline />}
          </button>
        </div>
        <div className={`${styles.mobileDropdown} ${showMobileToc ? styles.open : ''}`}>
          <ul>
            {toc.map((item) => (
              <li key={item.id} className={`${activeId === item.id ? styles.active : ''} ${item.level === 'H3' ? styles.subItem : ''}`}>
                <button onClick={() => scrollToHeading(item.id)}>{item.text}</button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* HERO */}
      <div className={styles.hero} style={{ backgroundImage: `url(${bannerImage})` }}>
        <div className={styles.heroOverlay}>
          <div className={styles.heroText}>
            <span className={styles.label}>Câu Chuyện Thương Hiệu</span>
            <h1>{pageData.title}</h1>
          </div>
        </div>
      </div>

      <div className={styles.mainContainer}>
        {/* SIDEBAR */}
        <aside className={styles.sidebar}>
          <Link to="/" className={styles.backBtn}><IoArrowBack /> Trang chủ</Link>
          <div className={styles.stickyContent}>
            <div className={styles.meta}>
              <span className={styles.author}>Hương Trầm Cổ Mộc</span>
              <span className={styles.time}><IoTimeOutline /> {readingTime}</span>
            </div>
            
            {toc.length > 0 && (
              <nav className={styles.tocWrapper}>
                <h4 className={styles.tocHeader}>NỘI DUNG CHÍNH</h4>
                <div className={styles.tocTree}>
                  {toc.map((item) => (
                    <div key={item.id} className={`${styles.tocNode} ${item.level === 'H3' ? styles.subNode : ''} ${activeId === item.id ? styles.active : ''}`}>
                      <div className={styles.indicator}></div>
                      <button onClick={() => scrollToHeading(item.id)}>{item.text}</button>
                    </div>
                  ))}
                </div>
              </nav>
            )}
          </div>
        </aside>

        {/* CONTENT */}
        <article className={styles.article}>
           {/* Render HTML đã xử lý */}
           <div 
              ref={contentRef}
              className={styles.htmlContent}
              dangerouslySetInnerHTML={{ __html: processedContent || pageData.content }} 
              onClick={handleContentClick}
           />
          <div className={styles.signature}>
            <p>Trân trọng,</p>
            <img src="/signature.png" alt="Chữ ký" onError={(e) => e.target.style.display='none'} />
            <h4>Hương Trầm Cổ Mộc</h4>
          </div>
        </article>
      </div>

      {/* LIGHTBOX COMPONENT */}
      <Lightbox
        open={openLightbox}
        close={() => setOpenLightbox(false)}
        index={lightboxIndex}
        slides={gallerySlides}
        plugins={[Zoom, Thumbnails]} 
        zoom={{ maxZoomPixelRatio: 3 }}
        animation={{ fade: 300 }}
        controller={{ closeOnBackdropClick: true }}
        styles={{ container: { backgroundColor: "rgba(20, 15, 10, 0.96)" } }}
      />
    </div>
  );
};

export default StoryPage;